import React, { useRef, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { showSnackbar } from './SnackbarAlert';
import MercatelyChat from './MercatelyChat'; // Importar el nuevo componente

const Chat = ({ selectedConversation }) => {
  const bottomRef = useRef(null);
  const [interactions, setInteractions] = useState([]);
  const [mercatelyJsonContent, setMercatelyJsonContent] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [fadeClass, setFadeClass] = useState('fade-enter');
  const [activeChat, setActiveChat] = useState('clarenta'); // 'clarenta' o 'mercately'


  useEffect(() => {
    const fetchInteractions = async () => {
      if (selectedConversation) {
        const interactionsRef = collection(db, `conversations/${selectedConversation.id}/llm-interactions`);
        const interactionsSnapshot = await getDocs(interactionsRef);

        const interactionData = interactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log(selectedConversation)
        setInteractions(interactionData);
      }
    };

    fetchInteractions();
    // Resetear a la vista de Clarenta cada vez que cambia la conversación
    setActiveChat('clarenta');
    setMercatelyJsonContent(null);
  }, [selectedConversation]);



  const formatTimestamp = (timestamp) => {
    if (timestamp.toDate) {
      // Firestore Timestamp object
      return new Date(timestamp.toDate()).toLocaleString();
    } else if (timestamp.seconds) {
      // Firestore Timestamp represented as an object { seconds, nanoseconds }
      return new Date(timestamp.seconds * 1000).toLocaleString();
    } else if (typeof timestamp === 'string' || timestamp instanceof Date) {
      // Timestamp is a string or a JS Date object
      return new Date(timestamp).toLocaleString();
    } else {
      // Fallback for unknown formats
      return 'Invalid date';
    }
  }

  const filteredInteractions = selectedMessage
    ? interactions.filter(interaction => interaction.messageId === selectedMessage)
    : [];

  const handleMessageClick = (messageId) => {
    setFadeClass('fade-exit');
    setTimeout(() => {
      setSelectedMessage(messageId);
      setFadeClass('fade-enter');
    }, 500);
  };

  const handleCopyGclid = async () => {
    try {
      const gclid = selectedConversation?.gclid;
      if (gclid) {
        await navigator.clipboard.writeText(gclid);
        showSnackbar("Gclid copied to clipboard", 'success');
      } else {
        showSnackbar("Gclid not available", 'warning');
      }
    } catch (err) {
      showSnackbar("Can not copy to clipboard", 'error');
    }
  };

  const handleCopyQuoteId = async () => {
    try {
      const quoteId = selectedConversation?.id;
      if (quoteId) {
        await navigator.clipboard.writeText(quoteId);
        showSnackbar("Quote ID copied to clipboard", 'success');
      } else {
        showSnackbar("Quote ID not available", 'warning');
      }
    } catch (err) {
      showSnackbar("Can not copy to clipboard", 'error');
    }
  };

  function renderLinks(text) {
    // Regex to match both [Link Text](URL) and [Link Text]{URL} formats
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)|\[([^\]]+)\]\{([^}]+)\}/g;
  
    const lines = text.split('\n').map((line, index) => {
      const parts = [];
      let lastIndex = 0;
  
      line.replace(linkRegex, (match, textContent, linkParenthesis, linkCurlyText, linkCurly, offset) => {
        // Determine the link text and URL based on which format matched
        const linkText = textContent || linkCurlyText;
        const link = linkParenthesis || linkCurly;
  
        // Add the text before the link
        if (offset > lastIndex) {
          parts.push(line.slice(lastIndex, offset));
        }
  
        // Add the link
        parts.push(
          <a
            key={`${index}-${offset}`}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue', textDecoration: 'underline' }}
          >
            {linkText}
          </a>
        );
        lastIndex = offset + match.length;
      });
  
      // Add any remaining text after the last link
      if (lastIndex < line.length) {
        parts.push(line.slice(lastIndex));
      }
  
      return (
        <span key={index}>
          {parts}
          <br /> {/* Add a line break */}
        </span>
      );
    });
  
    return lines;
  }
  
  

  if (!selectedConversation) {
    return <div className="text-gray-500 text-center">Select a conversation to view messages.</div>;
  }

  return (
    <div className="w-full">
      <div className="flex justify-center mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveChat('clarenta')}
          className={`px-4 py-2 text-sm font-medium ${
            activeChat === 'clarenta'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Conversación Clarenta
        </button>
        <button
          onClick={() => setActiveChat('mercately')}
          className={`px-4 py-2 text-sm font-medium ${
            activeChat === 'mercately'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Conversación Mercately
        </button>
      </div>

      <div className="flex flex-row" style={{ maxHeight: 'calc(78vh - 50px)' }}>
      <div
        className="flex-1 flex flex-col bg-white shadow-lg rounded-lg"
        style={{
          maxWidth: '500px',
          maxHeight: '78vh',
        }}
      >
        <div className="p-4 bg-gray-100 text-left text-sm text-gray-600">
          Chat on : {new Date(selectedConversation.timestamp?.toDate()).toLocaleString()}
          <br />
          Quote ID: {selectedConversation?.id ? (
            <span className="inline-flex items-center">
              {selectedConversation.id.slice(0, 25) + "..."}
              <button
                className="ml-2 p-1 bg-transparent hover:bg-gray-300 rounded"
                onClick={handleCopyQuoteId}
                aria-label="Copy Quote ID"
              >
                <ClipboardDocumentIcon className="h-5 w-5 text-gray-600" />
              </button>
            </span>
          ) : (
            'Not available'
          )}
          <br />
          Gclid: {selectedConversation?.gclid ? (
            <span className="inline-flex items-center">
              {selectedConversation.gclid.slice(0, 25) + "..."}
              <button
                className="ml-2 p-1 bg-transparent hover:bg-gray-300 rounded"
                onClick={handleCopyGclid}
                aria-label="Copy Gclid"
              >
                <ClipboardDocumentIcon className="h-5 w-5 text-gray-600" />
              </button>
            </span>
          ) : (
            'Not available'
          )}
        </div>

        {activeChat === 'clarenta' ? (
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            <div className="flex flex-col space-y-4">
              {selectedConversation.messages.map((message, idx) => (
                <div
                  key={idx}
                  onClick={() => handleMessageClick(idx)}
                  className={`p-3 rounded-lg max-w-xs cursor-pointer ${
                    message.role === 'user'
                      ? 'text-white self-end bg-[#4790CF]'
                      : message.role === 'system'
                      ? 'bg-purple-500 text-white self-center'
                      : 'bg-gray-300 text-black self-start'
                  } ${
                    selectedMessage === idx
                      ? 'border-2 border-green-500 shadow-lg'
                      : ''
                  }`}
                >
                  {/* Message Content */}
                  {message.role === 'assistant'
                    ? renderLinks(message.content)
                    : message.content.split('\n').map((line, lineIdx) => (
                        <span key={lineIdx}>
                          {line}
                          <br />
                        </span>
                      ))}

                  {/* Timestamp - Render if it exists */}
                  {message.timestamp && (
                    <div
                      className={`text-xs mt-2 text-right ${
                        message.role === 'user' ? 'text-white' : 'text-gray-500'
                      }`}
                    >
                      {formatTimestamp(message.timestamp)}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          </div>
        ) : (
          <MercatelyChat 
            selectedConversation={selectedConversation} 
            onJsonClick={setMercatelyJsonContent}
          />
        )}
      </div>

      <div
        className="flex-1 flex ml-8 flex-col bg-white shadow-lg rounded-lg"
        style={{
          minWidth: '300px',
          maxHeight: '78vh',
        }}
      >
        <div className="p-4 bg-gray-100 text-left text-sm text-gray-600">
          {activeChat === 'clarenta' ? "LLM Interactions (Select blue message)" : "Cotizaciones"}
        </div>
        <div className={`overflow-y-auto ${fadeClass}`} 
          style={{ 
            transition: 'opacity 0.2s ease', 
            opacity: fadeClass === 'fade-enter' ? 1 : 0 
          }}>
          {activeChat === 'clarenta' ? (
            <>
              {filteredInteractions.length > 0 ? (
                filteredInteractions.map(interaction => (
                  <div key={interaction.id} className="p-4 m-2 bg-gray-100 rounded-lg mb-4">
                    <h4 className="font-bold">Prompt {interaction.interactionId}</h4>
                    <p className="text-sm text-gray-600" style={{ whiteSpace: 'pre-wrap' }}>
                      {interaction.prompt}
                    </p>
                    <h4 className="font-bold">Response {interaction.interactionId}</h4>
                    <p className="text-sm text-gray-600" style={{ whiteSpace: 'pre-wrap' }}>
                      {interaction.response}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-center p-4">No interactions for this message.</div>
              )}
            </>
          ) : mercatelyJsonContent ? (
            (() => {
              try {
                const data = JSON.parse(mercatelyJsonContent);
                const userInfo = data.informacion_usuario;
                const quotes = data.cotizaciones;

                return (
                  <div className="p-4 overflow-y-auto" style={{maxHeight: 'calc(78vh - 100px)'}}>
                    {/* User Info Table */}
                    {userInfo && (
                      <div className="mb-6">
                        <h4 className="font-bold text-lg mb-2 text-gray-800">Información del Usuario</h4>
                        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                          <tbody className="text-gray-700 text-sm">
                            {Object.entries(userInfo).map(([key, value]) => (
                              <tr key={key} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="py-2 px-4 font-medium capitalize">{key.replace(/_/g, ' ')}</td>
                                <td className="py-2 px-4">{String(value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Quotes Tables */}
                    {quotes && Object.entries(quotes).map(([insurer, quoteData]) => (
                      <div key={insurer} className="mb-8">
                        <h4 className="font-bold text-xl mb-2 text-gray-800 capitalize">{insurer}</h4>
                        <p className="text-md font-semibold mb-3">
                          Precio: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(quoteData.precio)}
                        </p>
                        
                        <h5 className="font-semibold text-md mb-2 text-gray-700">Coberturas</h5>
                        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                          <thead className="bg-gray-100 text-gray-600 uppercase text-xs leading-normal">
                            <tr>
                              <th className="py-2 px-4 text-left">Cobertura</th>
                              <th className="py-2 px-4 text-left">Deducible</th>
                              <th className="py-2 px-4 text-left">Suma Asegurada</th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-700 text-sm">
                            {quoteData.coberturas && Object.entries(quoteData.coberturas).map(([coverageName, coverageDetails]) => (
                              <tr key={coverageName} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="py-2 px-4 capitalize">{coverageName.replace(/_/g, ' ')}</td>
                                <td className="py-2 px-4">{coverageDetails.deducible}</td>
                                <td className="py-2 px-4">{coverageDetails.suma_asegurada}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              } catch (error) {
                // Fallback to show raw JSON if parsing fails
                return (
                  <div className="p-4">
                    <p className="text-red-500 mb-2">Error al procesar el JSON. Mostrando datos en crudo.</p>
                    <pre className="text-left text-sm bg-gray-100 p-4 rounded-md overflow-auto max-h-[calc(78vh-120px)]">
                      <code>
                        {mercatelyJsonContent}
                      </code>
                    </pre>
                  </div>
                );
              }
            })()
          ) : (
            <div className="text-gray-500 text-center p-4">
              Selecciona un mensaje "JSON Cotizacion WS" para ver su contenido.
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Chat;
